import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Template } from "@/types/template";
import { useState, useEffect } from "react";

export const useTemplateOperations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdminRole = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const { data: roles, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .single();

        if (error) {
          console.error('Error fetching user role:', error);
          return;
        }

        setIsAdmin(roles?.role === 'admin');
      } catch (error) {
        console.error('Error checking admin role:', error);
      }
    };

    checkAdminRole();
  }, []);

  const { data: templates, isLoading, error: fetchError } = useQuery({
    queryKey: ['templates', isAdmin],
    queryFn: async () => {
      try {
        console.log('Fetching user templates...');
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          console.log('No active session found during templates fetch');
          throw new Error('User not authenticated');
        }

        // Select all templates with admin approval status
        let query = supabase
          .from('templates')
          .select(`
            *,
            processing_status,
            processing_started_at,
            processing_completed_at,
            processing_error,
            admin_approval_status,
            admin_uploaded_html,
            approved_by,
            approved_at,
            rejection_reason
          `)
          .order('created_at', { ascending: false });

        // For regular users, show only their own templates and approved company library templates
        if (!isAdmin) {
          query = query.or(`user_id.eq.${session.user.id},and(is_company_library.eq.true,admin_approval_status.eq.approved)`);
        }
        
        const { data, error } = await query;

        if (error) {
          console.error('Error fetching user templates:', error);
          throw error;
        }

        console.log('User templates fetched:', data);
        return data as Template[];
      } catch (error) {
        console.error('Template fetch error:', error);
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          throw new Error('Network error: Please check your internet connection');
        }
        throw error;
      }
    },
    retry: 2,
    retryDelay: 1000
  });

  const deleteMutation = useMutation({
    mutationFn: async (template: Template) => {
      console.log('Deleting template:', template);
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('User not authenticated');
      }

      // Delete associated documents first
      const { error: documentsError } = await supabase
        .from('documents')
        .delete()
        .eq('template_id', template.id);

      if (documentsError) {
        console.error('Error deleting associated documents:', documentsError);
        throw documentsError;
      }

      // Delete admin uploaded HTML file if exists
      if ((template as any).admin_uploaded_html) {
        const { error: adminHtmlError } = await supabase.storage
          .from('admin-html')
          .remove([(template as any).admin_uploaded_html]);

        if (adminHtmlError) {
          console.warn('Error deleting admin HTML file:', adminHtmlError);
        }
      }

      // Delete file from storage
      const { error: storageError } = await supabase.storage
        .from('templates')
        .remove([template.file_path]);

      if (storageError) {
        console.error('Error deleting file from storage:', storageError);
        throw storageError;
      }

      // Delete template from database
      const { error: dbError } = await supabase
        .from('templates')
        .delete()
        .eq('id', template.id);

      if (dbError) {
        console.error('Error deleting template from database:', dbError);
        throw dbError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast({
        title: "Success",
        description: "Template and associated documents deleted successfully",
      });
    },
    onError: (error) => {
      console.error('Delete mutation error:', error);
      toast({
        title: "Error",
        description: "Failed to delete template. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleDownload = async (template: Template) => {
    try {
      console.log('Downloading template:', template);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('User not authenticated');
      }

      // If template has admin uploaded HTML and is approved, download that instead
      if ((template as any).admin_uploaded_html && (template as any).admin_approval_status === 'approved') {
        const { data, error } = await supabase.storage
          .from('admin-html')
          .download((template as any).admin_uploaded_html);

        if (error) {
          console.error('Download error:', error);
          throw error;
        }

        const url = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = template.filename.replace(/\.[^/.]+$/, "") + '.html';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // Download original file
        const { data, error } = await supabase.storage
          .from('templates')
          .download(template.file_path);

        if (error) {
          console.error('Download error:', error);
          throw error;
        }

        const url = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = template.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      toast({
        title: "Success",
        description: "Template downloaded successfully",
      });
    } catch (error) {
      console.error('Error downloading template:', error);
      toast({
        title: "Error",
        description: "Failed to download template",
        variant: "destructive",
      });
    }
  };

  return {
    templates,
    isLoading,
    fetchError,
    deleteTemplate: deleteMutation.mutateAsync,
    handleDownload,
    retryFetch: () => queryClient.invalidateQueries({ queryKey: ['templates'] }),
    isAdmin,
  };
};
